import {
  applyBreakdownToLine,
  breakdownFromKg,
} from "@/common/stock/productPackagingConversion";

export default {
  methods: {
    lineUnitCode(line) {
      if (line.unit_code) {
        return line.unit_code;
      }
      if (line.unit && line.unit.code) {
        return line.unit.code;
      }
      const product = line.stock_product;
      if (product && product.primary_unit && product.primary_unit.code) {
        return product.primary_unit.code;
      }
      return "kg";
    },
    lineProductLabel(line) {
      if (line.stock_product) {
        return line.stock_product.label || line.stock_product.display_name || "-";
      }
      return line._product_label || "-";
    },
    lineProductMeta(line) {
      const product = line.stock_product;
      if (!product) {
        return "";
      }
      const packagingSummary = (product.packaging_levels || [])
        .map((level) => {
          const name = level.packaging?.label || level.packaging?.name || level.packaging_name;
          if (!name || level.weight_kg == null) {
            return null;
          }
          return `${name} ${level.weight_kg}kg`;
        })
        .filter(Boolean)
        .join(" / ");
      const parts = [
        product.code,
        product.stock_product_category ? product.stock_product_category.label : null,
        product.temperature ? product.temperature.label : null,
        packagingSummary || null,
        product.tags_subtitle,
        this.lineUnitCode(line) ? `${this.$t("STOCK.PRODUCTS.UNIT")}: ${this.lineUnitCode(line)}` : null,
      ].filter(Boolean);
      return parts.join(" · ");
    },
    ensureLineTotalNet(line, quantityKey) {
      const computed = this.computeLineNetFromUnit(line, quantityKey);
      if (line.line_total_net == null || line.line_total_net === "") {
        line.line_total_net = computed;
        return;
      }
      line.line_total_net = Number(line.line_total_net);
    },
    onLineQtyOrUnitInput(line, quantityKey) {
      line.line_total_net = this.computeLineNetFromUnit(line, quantityKey);
    },
    onLineTotalNetInput(line, quantityKey) {
      const qty = Number(line[quantityKey] ?? line.quantity ?? 0);
      const total = Number(line.line_total_net || 0);
      line.line_total_net = total;
      if (qty > 0) {
        line.unit_cost_net = this.roundMoney(total / qty);
      }
    },
    onLineVatRateInput() {
      // Document totals preview recalculates via computed property.
    },
    resolveLineUnitFromProduct(line, product) {
      const unit = product.primary_unit || product.unit;
      line.unit_id = product.primary_unit_id || (unit && unit.id) || line.unit_id || null;
      line.unit_code = (unit && unit.code) || "kg";
    },
    mapLineUnitFields(item) {
      const unit = item.unit || null;
      const product = item.stock_product || null;
      return {
        unit_id: item.unit_id ?? null,
        unit_code: unit?.code || product?.primary_unit?.code || "kg",
        unit: unit || null,
      };
    },
    initializeLinePackagingQuantities(line, product = null) {
      const sourceProduct = product || line.stock_product || null;
      if (!sourceProduct) {
        line.secondary_quantity = null;
        line.packaging_quantities = {};
        return line;
      }

      const breakdown = breakdownFromKg(sourceProduct, line.quantity || 0);
      applyBreakdownToLine(line, breakdown);
      return line;
    },
    mapLineFromApi(item, data, quantityKey) {
      const qty = Number(item[quantityKey] ?? item.quantity ?? 0);
      const unitCost = Number(item.unit_cost_net || 0);
      const lineTotal =
        item.line_total_net != null
          ? Number(item.line_total_net)
          : this.roundMoney(qty * unitCost);

      const line = {
        id: item.id,
        stock_product_id: item.stock_product_id,
        stock_product: item.stock_product || null,
        [quantityKey]: qty,
        unit_cost_net: unitCost,
        line_total_net: lineTotal,
        vat_rate: Number(item.vat_rate ?? data.default_vat_rate ?? 20),
        secondary_quantity: null,
        packaging_quantities: {},
        ...this.mapLineUnitFields(item),
      };

      return this.initializeLinePackagingQuantities(line);
    },
    addProductLine(product, emptyLineFactory) {
      this.insertProductLineAfter(product, emptyLineFactory, this.lines.length - 1);
    },
    insertProductLineAfter(product, emptyLineFactory, afterIndex) {
      const line = emptyLineFactory(this.document.default_vat_rate);
      line.stock_product_id = product.id;
      line.stock_product = product;
      this.resolveLineUnitFromProduct(line, product);
      this.initializeLinePackagingQuantities(line, product);
      if (typeof this.computeLineNetFromUnit === "function" && this.lineQuantityKey) {
        line.line_total_net = this.computeLineNetFromUnit(line, this.lineQuantityKey);
      }
      const insertAt = Math.min(Math.max(0, afterIndex + 1), this.lines.length);
      this.lines.splice(insertAt, 0, line);
      return line;
    },
    focusSelectAllInput(event) {
      const input =
        event?.target?.tagName === "INPUT"
          ? event.target
          : event?.target?.querySelector?.("input");
      if (!input) {
        return;
      }
      this.$nextTick(() => {
        input.focus();
        if (typeof input.select === "function") {
          input.select();
        } else if (input.setSelectionRange) {
          const len = String(input.value ?? "").length;
          input.setSelectionRange(0, len);
        }
      });
    },
  },
};
